import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
} from 'react'
import qs from 'query-string'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router'
import { toggleBookmark } from 'redux/actions/bookmarkActions'
import { fetchDetails, clearDetails } from 'redux/actions/detailActions'
import { useDispatch, useSelector } from 'react-redux'
import {
  buildUrl,
  getPlainComparePath,
  getSearchGetParams,
  getPageTypeFromUrl,
} from 'settings/app/urlHelpers'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import featureToggles from 'feature-toggles'

// config && partials
import {
  mappedEntryPointRoutes,
  entryPointId,
  __prod__,
  ADDITIONAL_SEARCHBAR_SELECTS,
} from 'settings/app/constants'
import { ACCORDION_ORDER, SCROLL_TO_ACCORDION, DetailView } from './partials/'

// components
import { Loader } from 'components/atoms'
import { MessageBox } from 'components/molecules'

/**
 * functional container for all detail views
 * @returns {*}
 * @constructor
 */
const Detail = () => {
  // hooks
  const history = useHistory()
  const location = useLocation()
  const textContext = useContext(TextContext)

  // remove trailing slash if exists
  const locationPathParts = location.pathname.replace(/\/$/, '').split('/')
  const detailId = locationPathParts.pop()
  const entryPointPath = locationPathParts.pop()

  // state and store
  const dispatch = useDispatch()
  const state = useSelector((state) => state)
  const { application, bookmarks, detailData, apiError } = state
  const pageType = useRef(getPageTypeFromUrl(entryPointPath))
  const DISCLAIMER_ANCHOR_ID = 'maintainable-text-disclaimer'
  const [departmentsOverlayActive, setDepartmentsOverlayActive] =
    useState(false)

  // render flags && order
  const [accordionOrder, setAccordionOrder] = useState([]) // rendering order for accordions on hospital view
  const [showCostCalculationButton, setShowCostCalculationButton] =
    useState(false) // flag for showing cost calculation (button in general information)
  const showBookmarkButton = bookmarks.totalCount > 0
  const showCompareButton =
    bookmarks.types[entryPointId.HOSPITALS].count > 1 ||
    bookmarks.types[entryPointId.DOCTORS].count > 1

  // load details on mount, generate back path; depends on search type
  const getBackPath = useCallback(() => {
    const { lat, lng, ...parsed } = qs.parse(location.search)

    const [_, getUrl] = buildUrl(state, {
      key: parsed.key,
      type: parsed.type,
      lat,
      lng,
    })

    return {
      to: `/${ getUrl }`,
    }
  }, [location, state])

  // scroll top on load
  React.useEffect(() => {
    setTimeout(
      () =>
        window.scrollTo({
          top: __prod__
            ? document.querySelector('header').getBoundingClientRect().top
            : 0,
          behavior: 'smooth',
        }),
      0
    )
  }, [detailData])

  /**
   * xhr detail request
   */
  useEffect(() => {
    // clear details to show loader on page load
    dispatch(clearDetails())
    dispatch(fetchDetails(pageType.current, detailId, location.search))
  }, [dispatch, detailId, entryPointPath, location.search])

  /**
   *  set accordion order depending on result type
   *  case hospitals has two different (with treatment reason and without)
   */
  useEffect(() => {
    if (pageType.current === entryPointId.HOSPITALS && detailData !== null) {
      if (detailData.is_treatment_reason) {
        setAccordionOrder(ACCORDION_ORDER.HOSPITAL_WITH_TREATMENT_REASON)
      } else {
        setAccordionOrder(ACCORDION_ORDER.HOSPITAL_WITH_OUT_TREATMENT_REASON)
      }
    } else {
      switch (pageType.current) {
        case entryPointId.CAREHOMES:
          setAccordionOrder(ACCORDION_ORDER.CAREHOMES)
          break
        case entryPointId.DAYANDNIGHTCARE:
          setAccordionOrder(ACCORDION_ORDER.DAYANDNIGHTCARE)
          break
        case entryPointId.CARESERVICES:
          setAccordionOrder(ACCORDION_ORDER.CARESERVICES)
          break
        case entryPointId.CARESUPPORT:
          setAccordionOrder(ACCORDION_ORDER.CARESUPPORT)
          break
        case entryPointId.DOCTORS:
        default:
          setAccordionOrder(ACCORDION_ORDER.DOCTORS)
      }
    }
  }, [detailData])

  /**
   * set flag for showing cost calculation (button in general information)
   */
  useEffect(() => {
    if (
      featureToggles.costEstimate &&
      pageType.current === entryPointId.CARESERVICES
    ) {
      setShowCostCalculationButton(true)

      const searchUrlParams = qs.parse(location.search)
      const additionalSearchKey = ADDITIONAL_SEARCHBAR_SELECTS[
        entryPointId.CARESERVICES
      ].find((_, i) =>
        ADDITIONAL_SEARCHBAR_SELECTS[entryPointId.CARESERVICES][
          i
        ].hasOwnProperty('showCalc')
      )

      if (
        searchUrlParams[additionalSearchKey.typ] &&
        additionalSearchKey.showCalc.includes(
          searchUrlParams[additionalSearchKey.typ]
        )
      ) {
        setShowCostCalculationButton(true)
      } else {
        setShowCostCalculationButton(false)
      }
    }
  }, [location.search])

  const toggleBookmarkHandler = () => {
    const parsed = qs.parse(location.search)

    dispatch(
      toggleBookmark({
        id: detailId,
        type: pageType.current,
        searchParams: {
          ...getSearchGetParams({
            resultType: pageType.current,
            prevParams: parsed,
          }),
        },
      })
    )
  }

  const toggleOverlayHandler = (visible) => {
    setDepartmentsOverlayActive(visible)
  }

  /** no detail link if
   * #1) 'hospitalIdentifier' doesn't exist
   * #2) 'hospitalIdentifier' is same as detailId
   **/
  const getDetailLink = () => {
    if (
      !detailData.hospitalIdentifier ||
      detailData.hospitalIdentifier === detailId
    )
      return null

    return {
      to: `${ mappedEntryPointRoutes.hospitals }/${ detailData.hospitalIdentifier }/${ location.search }`,
    }
  }

  const getMaintainableDisclaimer = (id) => {
    const { maintainableTextDisclaimer } = detailData.result
    if (!maintainableTextDisclaimer) return null
    return maintainableTextDisclaimer.indexOf(id) > -1
      ? DISCLAIMER_ANCHOR_ID
      : null
  }

  const getLoader = () => (
    <Loader variant='loading'>{textContext['general.loadingData']}</Loader>
  )

  const getDetailHeadline = (data, id) => {
    let headline = textContext['general.navigatorTitle']

    switch (id) {
      case entryPointId.CAREHOMES:
      case entryPointId.DAYANDNIGHTCARE:
      case entryPointId.CARESUPPORT:
      case entryPointId.CARESERVICES:
        headline = textContext['general.careNavigatorTitle']
        headline = `${ headline }: ${ data.name }`
        break

      case entryPointId.DOCTORS:
        headline = `${ headline }: ${ data.name }`
        break

      case entryPointId.HOSPITALS:
      default:
        headline = `${ headline }: ${ data.locationName }`
    }

    return headline
  }

  const getBookmarkState = () => {
    if (!bookmarks.types[pageType.current]) {
      console.error(
        `pageType (${ pageType.current }) is not defined in bookmarks.types`
      )
      return false
    }
    return bookmarks.types[pageType.current].items.indexOf(detailId) > -1
  }

  /**
   * get scroll to elements
   * @returns {boolean|*}
   */
  const getScrollToElements = () => {
    const scrollElements = SCROLL_TO_ACCORDION[pageType.current]
    return typeof scrollElements !== 'undefined' && scrollElements.length > 0
      ? scrollElements
      : false
  }

  const mapLocationDepartments = (data) => {
    const mappedLinkData = Object.keys(data).map((key) => ({
      label: key,
      suffix: `${ data[key] } ${ textContext['general.treatments.short'] }`,
    }))

    return [{ linkList: [...mappedLinkData] }]
  }

  if (apiError) {
    return <MessageBox isError={ true } showClose={ false } />
  }

  // TODO: loader should also depend on request
  if (!detailData || !detailData.result) {
    return getLoader()
  }

  return (
    <DetailView
      detailData={ detailData }
      accordionOrder={ accordionOrder }
      backPath={ getBackPath() }
      detailLink={ getDetailLink() }
      detailHeadline={ getDetailHeadline(detailData.result, pageType.current) }
      bookmarkCount={ bookmarks.totalCount }
      onBookmarkButtonClicked={ () =>
        showBookmarkButton &&
        history.push({
          pathname: '/merkliste',
          state: { lastEntryPoint: pageType.current },
        })
      }
      onCompareButtonClicked={ () =>
        showCompareButton &&
        history.push(getPlainComparePath(application.entryPoint, 'local'))
      }
      onToggleOverlay={ () => toggleOverlayHandler() }
      departmentsOverlayActive={ departmentsOverlayActive }
      bookmarkState={ getBookmarkState() }
      onToggleBookmark={ () => toggleBookmarkHandler() }
      scrollElements={ getScrollToElements() }
      disclaimerAnchorId={ DISCLAIMER_ANCHOR_ID }
      getMaintainableDisclaimer={ (id) => getMaintainableDisclaimer(id) }
      pageType={ pageType }
      mapLocationDepartments={ (data) => mapLocationDepartments(data) }
      showCostCalculationButton={ showCostCalculationButton }
    />
  )
}

export default Detail
